import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Label, Col, Row } from "reactstrap";
import { checkFinalPrice } from "../checkFinalPrice";
import {
  fetchItemsByRestaurantPaginated,
  fetchOffersByRestaurantPaginated,
} from "store/actions/itemsActions";
import { AsyncPaginate } from "react-select-async-paginate";
import { addToOrder } from "store/actions/cartAction";
import { toast } from "react-toastify";

const SelectItemsOffers = ({ restaurantID, currency }) => {
  const dispatch = useDispatch();
  const { restaurantsDetail } = useSelector((state) => state.restaurant);
  const { orderItems } = useSelector((state) => state.cart);

  const [selectedItem, setSelectedItem] = useState("");
  const [selectedOffer, setSelectedOffer] = useState("");

  const loadItemsFromAlgolia = async (searchQuery, loadedOptions, { page }) => {
    try {
      const { items, totalPages: total } = await dispatch(
        fetchItemsByRestaurantPaginated({
          restaurantID,
          page,
          hitsPerPage: 10,
          query: searchQuery,
        })
      );

      const formattedOptions = items.flatMap((item) => {
        const {
          id,
          variants = [],
          labels,
          label: dblabel,
          addOns,
          ...rest
        } = item;

        const images = Array.isArray(item?.images) ? [...item?.images] : [];
        // Default to one option if no or one variant
        if (variants.length <= 1) {
          const variant = variants[0] || {};
          const sizeKey = variant.size || "default";
          const compositeKey = `${id}__${sizeKey}`;

          return [
            {
              ...rest,
              label: `${item?.name} (${variant.size}: ${currency}${variant.price})`,
              value: compositeKey,
              id,
              name: item?.name,
              images,
              addons: addOns,
              selectedAddons: [],
              currency,
              type: "item",
              notes: "",
              size: variant.size || null,
              price: variant.price || item.price || 0,
              totalPrice: variant.price || item.price || 0,
            },
          ];
        }

        // Multiple variants
        return variants.map((variant) => {
          const compositeKey = `${id}__${variant.size}`;
          return {
            ...rest,
            label: `${item?.name} (${variant.size}: ${currency}${variant.price})`,
            value: compositeKey,
            id,
            name: item?.name,
            images,
            addons: addOns,
            selectedAddons: [],
            type: "item",
            currency,
            size: variant.size,
            price: variant.price,
            totalPrice: variant.price,
            notes: "",
          };
        });
      });

      return {
        options: formattedOptions,
        hasMore: page + 1 < total,
        additional: {
          page: page + 1,
        },
      };
    } catch (error) {
      console.error("Error loading options:", error);
      return {
        options: [],
        hasMore: false,
        additional: {
          page: page,
        },
      };
    }
  };

  const loadOffersFromAlgolia = async (
    searchQuery,
    loadedOptions,
    { page }
  ) => {
    try {
      const { offers, totalPages: total } = await dispatch(
        fetchOffersByRestaurantPaginated({
          restaurantID,
          page,
          hitsPerPage: 5,
          query: searchQuery,
        })
      );

      const formattedOptions = offers.flatMap((offer) => {
        const { id, image, ...rest } = offer;

        return [
          {
            ...rest,
            label: `${offer?.title} (${currency}${offer.price})`,
            value: id || "sfsdfdsf",
            id,
            name: offer?.title,
            images: image || "",
            cartItems: Array.isArray(offer.items)
              ? [...offer.items]
              : offer.items
              ? [offer.items]
              : [],
            type: "offer",
            currency,
            notes: "",
            price: offer.price || 0,
            totalPrice: offer.price || 0,
          },
        ];
      });

      return {
        options: formattedOptions,
        hasMore: page + 1 < total,
        additional: {
          page: page + 1,
        },
      };
    } catch (error) {
      console.error("Error loading options:", error);
      return {
        options: [],
        hasMore: false,
        additional: {
          page: page,
        },
      };
    }
  };

  const handleSelectItem = (item) => {
    const itemPrice = parseFloat(item?.price) || 0;

    const simulatedFinal = checkFinalPrice({
      cartItems: orderItems,
      restaurant: restaurantsDetail,
      itemPrice: itemPrice,
    });

    if (simulatedFinal > 10000) {
      toast.error("Can't add order of value more than 10000");
      return;
    }
    if (!item || !item.value) return;

    const { label, comments, recommendedProducts, objectID, menuID, categoryId, isVisible, createdAt, updatedAt, _highlightResult,...itemWithSize } = item;

    dispatch(addToOrder(itemWithSize));

    setSelectedItem(item?.name);
  };

  const handleSelectOffer = (offer) => {
    const offerPrice = parseFloat(offer?.price) || 0;

    const simulatedFinal = checkFinalPrice({
      cartItems: orderItems,
      restaurant: restaurantsDetail,
      itemPrice: offerPrice,
    });

    if (simulatedFinal > 10000) {
      toast.error("Can't add order of value more than 10000");
      return;
    }
    if (!offer || !offer.value) return;

    const { label, items, createdAt, title, objectID, _highlightResult, ...offerWithItems } = offer;

    dispatch(addToOrder(offerWithItems));

    setSelectedOffer(offer?.name);
  };

  return (
    <>
      <Row className="w-100 mx-0 mb-3 py-0 justify-content-center">
        <Col className="col-12 col-lg-6 mb-3 mb-lg-0 px-0 pr-lg-2">
          <Label className="mb-0">Choose item</Label>
          <AsyncPaginate
            debounceTimeout={500}
            isDisabled={false}
            value={selectedItem}
            onChange={handleSelectItem}
            placeholder="Select or search an item..."
            loadOptions={loadItemsFromAlgolia}
            additional={{ page: 0 }}
            menuPortalTarget={
              typeof window !== "undefined" ? document.body : null
            }
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </Col>

        <Col className="col-12 col-lg-6 mb-0 px-0 pl-lg-2">
          <Label className="mb-0">Choose offer:</Label>
          <AsyncPaginate
            debounceTimeout={500}
            isDisabled={false}
            value={selectedOffer}
            onChange={handleSelectOffer}
            placeholder="Select or search an offer..."
            loadOptions={loadOffersFromAlgolia}
            additional={{ page: 0 }}
              menuPortalTarget={
              typeof window !== "undefined" ? document.body : null
            }
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </Col>
      </Row>
    </>
  );
};

export default SelectItemsOffers;
